import gsap from 'gsap/all';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAnimationContext } from '../context/AnimationContext';
import { useNavigationContext } from '../context/NavigationContext';
import { SectionIDs } from '../data/Sections';
import styles from './styles.module.scss';
import {useTranslation} from 'react-i18next';

const masterStyle: React.CSSProperties = {};

export enum DivisionIDs {
  Intro = 'intro',
  Joke = 'joke',
  Science = 'science',
  Conclusion = 'conclusion',
}

export default function LeftNavigation(): JSX.Element
{
  const { t } = useTranslation('ai4Jokes');
  const {activeSectionID} = useNavigationContext();

  const { AddAnimations } = useAnimationContext();
  const title1Ref = useRef<HTMLSpanElement>(null);
  const title2Ref = useRef<HTMLSpanElement>(null);
  const title3Ref = useRef<HTMLSpanElement>(null);
  const title4Ref = useRef<HTMLSpanElement>(null);
  const line1Ref = useRef<HTMLDivElement>(null);
  const line2Ref = useRef<HTMLDivElement>(null);
  const line3Ref = useRef<HTMLDivElement>(null);
  const line4Ref = useRef<HTMLDivElement>(null);
  const number1Ref = useRef<HTMLDivElement>(null);
  const number2Ref = useRef<HTMLDivElement>(null);
  const number3Ref = useRef<HTMLDivElement>(null);
  const number4Ref = useRef<HTMLDivElement>(null);

  const [currentDivisionId, setCurrentDivisionId] = useState<string | null>(null);

  useEffect(() => {
    switch (activeSectionID) {
    case SectionIDs.Intro1:
    case SectionIDs.Intro2:
    case SectionIDs.Intro3:
    case SectionIDs.TellMeAJoke:
      if(currentDivisionId !== DivisionIDs.Intro){
        setCurrentDivisionId(DivisionIDs.Intro);
        closeAllAnim();
        playIntroAnim();
      }
      break;
    case SectionIDs.Joke:
      if(currentDivisionId !== DivisionIDs.Joke){
        setCurrentDivisionId(DivisionIDs.Joke);
        closeAllAnim();
        playJokeAnim();
      }
      break;
    case SectionIDs.Science:
      if(currentDivisionId !== DivisionIDs.Science){
        setCurrentDivisionId(DivisionIDs.Science);
        closeAllAnim();
        playScienceAnim();
      }
      break;
    case SectionIDs.Conclusion:
    case SectionIDs.About:
      if(currentDivisionId !== DivisionIDs.Conclusion){
        setCurrentDivisionId(DivisionIDs.Conclusion);
        closeAllAnim();
        playConclusionAnim();
      }
      break;
    default:
      break;
    }
    // We don't want currentDivisionId to update this effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionID]);



  const closeAllAnim = useCallback(
    () => {
      gsap.to(line1Ref.current, { height: '0rem', opacity: '0', duration: 0 });
      gsap.to(title1Ref.current, { opacity: '0', left: '-1.2rem', duration: 0 });
      gsap.to(number1Ref.current, { color: '#c9c9c9', duration: 0 });

      gsap.to(line2Ref.current, { height: '0rem', opacity: '0', duration: 0 });
      gsap.to(title2Ref.current, { opacity: '0', left: '-1.2rem', duration: 0 });
      gsap.to(number2Ref.current, { color: '#c9c9c9', duration: 0 });

      gsap.to(line3Ref.current, { height: '0rem', opacity: '0', duration: 0 });
      gsap.to(title3Ref.current, { opacity: '0', left: '-1.2rem', duration: 0 });
      gsap.to(number3Ref.current, { color: '#c9c9c9', duration: 0 });

      gsap.to(line4Ref.current, { height: '0rem', opacity: '0', duration: 0 });
      gsap.to(title4Ref.current, { opacity: '0', left: '-1.2rem', duration: 0 });
      gsap.to(number4Ref.current, { color: '#c9c9c9', duration: 0 });
    },
    []);


  const playIntroAnim = useCallback(
    () => {
      gsap.fromTo(line1Ref.current, { height: '0rem', opacity: '0' }, { height: '7.5rem', opacity: '1', duration: 0.5 });
      gsap.fromTo(title1Ref.current, { opacity: '0', left: '-1.2rem' }, { opacity: '1', left: '0rem', duration: 0.3, delay: 0.3 });
      gsap.fromTo(number1Ref.current, { color: '#c9c9c9' }, { color: '#212227', duration: 0.5 });
    },
    []);

  const playJokeAnim = useCallback(
    () => {
      gsap.fromTo(line2Ref.current, { height: '0rem', opacity: '0' }, { height: '5rem', opacity: '1', duration: 0.5 });
      gsap.fromTo(title2Ref.current, { opacity: '0', left: '-1.2rem' }, { opacity: '1', left: '0rem', duration: 0.3, delay: 0.3 });
      gsap.fromTo(number2Ref.current, { color: '#c9c9c9' }, { color: '#212227', duration: 0.5 });
    },
    []);

  const playScienceAnim = useCallback(
    () => {
      gsap.fromTo(line3Ref.current, { height: '0rem', opacity: '0' }, { height: '7.4rem', opacity: '1', duration: 0.5 });
      gsap.fromTo(title3Ref.current, { opacity: '0', left: '-1.2rem' }, { opacity: '1', left: '0rem', duration: 0.3, delay: 0.3 });
      gsap.fromTo(number3Ref.current, { color: '#c9c9c9' }, { color: '#212227', duration: 0.5 });
    },
    []);


  const playConclusionAnim = useCallback(
    () => {
      gsap.fromTo(line4Ref.current, { height: '0rem', opacity: '0' }, { height: '7.5rem', opacity: '1', duration: 0.5 });
      gsap.fromTo(title4Ref.current, { opacity: '0', left: '-1.2rem' }, { opacity: '1', left: '0rem', duration: 0.3, delay: 0.3 });
      gsap.fromTo(number4Ref.current, { color: '#c9c9c9' }, { color: '#212227', duration: 0.5 });
    },
    []);

  useEffect(() =>
  {
    // #########################################
    // PART 1
    AddAnimations([{
      targets: masterStyle,
      from: { display: 'none' },
      to: { display: 'flex' },
      start: 0,
      end: 0.1
    },
    {
      targets: masterStyle,
      from: { opacity: '0' },
      to: { opacity: '1' },
      start: 0.2,
      end: 0.3
    }
    ], SectionIDs.Intro1);
    AddAnimations([
      {
        targets: line1Ref.current,
        from: { height: '7.5rem', opacity: '1' },
        to: { height: '0rem', opacity: '0' },
        start: 0.9,
        end: 1
      },
      {
        targets: title1Ref.current,
        from: { opacity: '1', left: '0rem' },
        to: { opacity: '0', left: '-1.2rem' },
        start: 0.8,
        end: 0.9
      },
      {
        targets: number1Ref.current,
        from: { color: '#212227' },
        to: { color: '#c9c9c9' },
        start: 0.9,
        end: 1
      }
    ], SectionIDs.TellMeAJoke);
    // #########################################


    // // #########################################
    // // PART 2
    // AddAnimations([
    //   {
    //     targets: line2Ref.current,
    //     from: { height: '0rem', opacity: '0' },
    //     to: { height: '5rem', opacity: '1' },
    //     start: 0.1,
    //     end: 0.2
    //   },
    //   {
    //     targets: title2Ref.current,
    //     from: { opacity: '0', left: '-1.2rem' },
    //     to: { opacity: '1', left: '0rem' },
    //     start: 0.2,
    //     end: 0.3
    //   },
    //   {
    //     targets: number2Ref.current,
    //     from: { color: '#c9c9c9' },
    //     to: { color: '#212227' },
    //     start: 0.2,
    //     end: 0.3
    //   }
    // ], SectionIDs.Joke);
    AddAnimations([
      {
        targets: line2Ref.current,
        from: { height: '5rem', opacity: '1' },
        to: { height: '0rem', opacity: '0' },
        start: 0.9,
        end: 1
      },
      {
        targets: title2Ref.current,
        from: { opacity: '1', left: '0rem' },
        to: { opacity: '0', left: '-1.2rem' },
        start: 0.8,
        end: 0.9
      },
      {
        targets: number2Ref.current,
        from: { color: '#212227' },
        to: { color: '#c9c9c9' },
        start: 0.9,
        end: 1
      }
    ], SectionIDs.Joke);
    // #########################################


    // // #########################################
    // // PART 3
    // AddAnimations([
    //   {
    //     targets: line3Ref.current,
    //     from: { height: '0rem', opacity: '0' },
    //     to: { height: '7.4rem', opacity: '1' },
    //     start: 0.1,
    //     end: 0.2
    //   },
    //   {
    //     targets: title3Ref.current,
    //     from: { opacity: '0', left: '-1.2rem' },
    //     to: { opacity: '1', left: '0rem' },
    //     start: 0.2,
    //     end: 0.3
    //   },
    //   {
    //     targets: number3Ref.current,
    //     from: { color: '#c9c9c9' },
    //     to: { color: '#212227' },
    //     start: 0.2,
    //     end: 0.3
    //   }
    // ], SectionIDs.Science);
    AddAnimations([
      {
        targets: line3Ref.current,
        from: { height: '7.4rem', opacity: '1' },
        to: { height: '0rem', opacity: '0' },
        start: 0.9,
        end: 1
      },
      {
        targets: title3Ref.current,
        from: { opacity: '1', left: '0rem' },
        to: { opacity: '0', left: '-1.2rem' },
        start: 0.8,
        end: 0.9
      },
      {
        targets: number3Ref.current,
        from: { color: '#212227' },
        to: { color: '#c9c9c9' },
        start: 0.9,
        end: 1
      }
    ], SectionIDs.Science);
    // #########################################


    // #########################################
    // PART 4
    // AddAnimations([
    //   {
    //     targets: line4Ref.current,
    //     from: { height: '0rem', opacity: '0' },
    //     to: { height: '7.5rem', opacity: '1' },
    //     start: 0.1,
    //     end: 0.2
    //   },
    //   {
    //     targets: title4Ref.current,
    //     from: { opacity: '0', left: '-1.2rem' },
    //     to: { opacity: '1', left: '0rem' },
    //     start: 0.2,
    //     end: 0.3
    //   },
    //   {
    //     targets: number4Ref.current,
    //     from: { color: '#c9c9c9' },
    //     to: { color: '#212227' },
    //     start: 0.2,
    //     end: 0.3
    //   }
    // ], SectionIDs.Conclusion);
    // AddAnimations([
    //   {
    //     targets: line4Ref.current,
    //     from: { height: '7.5rem', opacity: '1' },
    //     to: { height: '0rem', opacity: '0' },
    //     start: 0.9,
    //     end: 1
    //   },
    //   {
    //     targets: title4Ref.current,
    //     from: { opacity: '1', left: '0rem' },
    //     to: { opacity: '0', left: '-1.2rem' },
    //     start: 0.8,
    //     end: 0.9
    //   },
    //   {
    //     targets: number4Ref.current,
    //     from: { color: '#212227' },
    //     to: { color: '#c9c9c9' },
    //     start: 0.9,
    //     end: 1
    //   }
    // ], SectionIDs.About);
    // // #########################################

    AddAnimations([
      {
        targets: line4Ref.current,
        from: { borderColor: '#212227' },
        to: { borderColor: '#c9c9c9' },
        start: 0,
        end: 0.1
      },
      {
        targets: title4Ref.current,
        from: { color: '#212227' },
        to: { color: '#c9c9c9' },
        start: 0,
        end: 0.1
      },
      {
        targets: number4Ref.current,
        to: { color: '#c9c9c9' },
        start: 0,
        end: 0.1
      }
    ], SectionIDs.About);
  }, [ AddAnimations ]);

  return (
    <div className={styles.leftNavigation} style={{...masterStyle}}>
      <div ref={number1Ref} className={styles.number}>01</div>
      <div className={styles.title}>
        <div ref={line1Ref} className={styles.line}>
          <span ref={title1Ref}>{t('leftnavigation.intro')}</span>
        </div>
      </div>
      <div ref={number2Ref} className={styles.number}>02</div>
      <div className={styles.title}>
        <div ref={line2Ref} className={styles.line}>
          <span ref={title2Ref}>{t('leftnavigation.joke')}</span>
        </div>
      </div>
      <div ref={number3Ref} className={styles.number}>03 </div>
      <div className={styles.title}>
        <div ref={line3Ref}
          className={styles.line}>
          <span ref={title3Ref}>{t('leftnavigation.science')}</span>
        </div>
      </div>
      <div ref={number4Ref} className={styles.number}>04</div>
      <div className={styles.title}>
        <div ref={line4Ref} className={styles.line}>
          <span ref={title4Ref}>{t('leftnavigation.conclusion')}</span>
        </div>
      </div>
    </div>
  );
}
